import type { Exercise } from '@fluent/types';
import { bareWord, normalizeAnswer } from '../lesson/matchAnswer.js';
import { IRREGULAR_BASES, IRREGULAR_NOUNS, IRREGULAR_VERB_FORMS, IRREGULAR_WORDS, SHORT_NEGATIVES } from '../lesson/irregularForms.js';
import { phraseChoiceEnglishTexts } from '../lesson/phraseChoiceText.js';
import { getCourseTree, isMainCourseLessonId } from './course.js';
import { getLesson, listLessonIds } from './lessons.js';

/**
 * Every English word the course teaches — in phrase cards, pairs, dialogues
 * and right answers. The answer matcher uses it to tell a typo
 * from a real-word mistake: "techer" is a typo (close), but "work" for
 * "works" or "a" for "an" lands on another word the learner was taught, so
 * it is a grammar mistake (wrong). GRAMMAR-REVIEW-2026-10-07 § 3.
 *
 * Built once, lazily, from the bundled lessons (they are already in the
 * main bundle — see lessons.ts) — the MAIN course's only. The phrases course
 * (PHRASES.md § 3) would add B1–C2 vocabulary, and every extra word can turn
 * a main-course one-letter typo from "close" into a "wrong" real-word swap.
 */
let cache: ReadonlySet<string> | null = null;

/** The main course's lessons — the skill tree's, in curriculum order. */
export function knownWordsLessonIds(): string[] {
  return getCourseTree()?.orderedLessonIds ?? listLessonIds().filter(isMainCourseLessonId);
}

export function getKnownWords(): ReadonlySet<string> {
  if (cache) return cache;
  const words = new Set<string>();
  for (const id of knownWordsLessonIds()) {
    const lesson = getLesson(id);
    if (!lesson) continue;
    for (const ex of lesson.exercises) {
      for (const text of englishTexts(ex)) addWords(words, text);
    }
  }
  // Irregular verbs and plurals the course may never print (irregularForms.ts).
  for (const w of IRREGULAR_WORDS) words.add(w);
  // Every negative short form is a word ("hadn't" for "hasn't" is a tense slip).
  for (const w of SHORT_NEGATIVES) words.add(w);
  cache = words;
  return words;
}


/** Test seam: drop the memoized sets. */
export function resetKnownWordsForTests(): void {
  cache = null;
  verbCache = null;
  nounCache = null;
}

/**
 * The words the course uses as VERBS: whatever follows a subject pronoun, a
 * modal or do/does/did in a right answer ("I eat", "can swim", "mustn't
 * feed"), plus the irregular verbs. The matcher calls an added -s / -d a
 * grammar slip only on these: "I eats" and "I liked" are mistakes, "years
 * olds" and "a niced day" are typos (2026-10-08 typo sweep).
 */
let verbCache: ReadonlySet<string> | null = null;
// Not "to": "to school", "to bed", "to music" are places and things.
const VERB_CUES = new Set([
  'i', 'you', 'we', 'they', 'he', 'she', 'can', "can't", 'cannot', 'will', "won't",
  'would', "wouldn't", 'should', "shouldn't", 'must', "mustn't", 'do', "don't", 'does',
  "doesn't", 'did', "didn't", "let's",
]);
const PRONOUN_CUES = new Set(['i', 'you', 'we', 'they', 'he', 'she']);
/** A noun phrase ends before these: "my mother is", "the room and". */
const PHRASE_END = new Set([
  'is', 'are', 'am', 'was', 'were', 'has', 'have', 'and', 'or', 'but', 'in', 'on', 'at',
  'to', 'for', 'with', 'from', 'of', 'near', 'next', 'every', 'today', 'tomorrow',
  'yesterday', 'tonight', 'now', 'here', 'there', 'please', 'because', 'so', 'when',
  'while', 'who', 'which', 'that', 'can', 'will', "isn't", "aren't", "doesn't", "don't",
  "can't", "won't", 'is', 'too', 'very',
]);
const BE = new Set(['am', 'is', 'are', 'was', 'were', "isn't", "aren't", "wasn't", "weren't"]);
const NOT_VERBS = new Set([
  'always', 'usually', 'often', 'never', 'sometimes', 'also', 'really', 'just', 'already',
  'still', 'even', 'not', 'very', 'so', 'too', 'the', 'a', 'an', 'my', 'your', 'his', 'her',
  'our', 'their', 'this', 'that', 'it', 'me', 'him', 'us', 'them', 'am', 'are', 'is',
  'today', 'tomorrow', 'tonight', 'yesterday', 'ever', 'first', 'last', 'later', 'next',
  'i', 'you', 'we', 'they', 'he', 'she', 'there', 'here',
]);

/** "have to decide", "want to book": after these, "to" leads a verb. */
const TO_AFTER = new Set([
  'have', 'has', 'had', 'want', 'wants', 'like', 'likes', 'need', 'needs', 'going', 'learn',
  'try', 'tried', 'decide', 'decided', 'hope', 'forgot', 'remember', 'love', 'hate', 'used',
  'able', 'ready', 'nice', 'easy', 'hard',
]);
const ADJECTIVE_CUES = new Set(['is', 'are', 'am', 'was', 'were', 'be', 'been', 'very', 'so', 'too', 'quite', 'really']);
const STRONG_ADJECTIVE_CUES = new Set(['very', 'so', 'too', 'quite', 'really']);
/** Never nouns, though they can end a determiner phrase ("one, please",
 *  "this before", "twice a week"). */
const NOT_NOUNS = new Set([
  ...'please twice once before after ago again yet here there home now then today tomorrow tonight yesterday'.split(' '),
  ...'one two three four five six seven eight nine ten eleven twelve hundred thousand'.split(' '),
  ...'more most less least other another same own later interesting boring amazing'.split(' '),
]);
/** Nouns that only look like superlatives. */
const EST_NOUNS = new Set('forest rest test guest chest interest nest west east contest request'.split(' '));
function isSuperlative(word: string): boolean {
  return word.endsWith('est') && !EST_NOUNS.has(word);
}
/** Determiners: the word they lead to is a noun when the phrase ends there. */
const DETERMINERS = new Set([
  'a', 'an', 'the', 'my', 'your', 'his', 'her', 'our', 'their', 'this', 'that', 'these',
  'those', 'some', 'any', 'every', 'one', 'no', 'each',
]);

let nounCache: ReadonlySet<string> | null = null;

/** A verb or its he/she form: "take", "takes", "goes", "hurts". */
export function isVerbForm(verbs: ReadonlySet<string>, word: string): boolean {
  if (verbs.has(word)) return true;
  if (!word.endsWith('s')) return false;
  return verbs.has(word.slice(0, -1)) || (word.endsWith('es') && verbs.has(word.slice(0, -2)));
}

function buildClasses(): void {
  const verbs = new Set<string>([...IRREGULAR_BASES, ...IRREGULAR_VERB_FORMS]);
  const texts: string[][] = [];
  for (const id of knownWordsLessonIds()) {
    for (const ex of getLesson(id)?.exercises ?? []) {
      for (const text of englishTexts(ex)) texts.push(text.replace(/[\u2018\u2019]/gu, "'").split(/\s+/u));
    }
  }
  const at = (raw: string[], k: number) => bareWord(normalizeAnswer(raw[k] ?? ''));

  // Pass 1 — verbs: after a subject pronoun, a modal, do/does/did or "have
  // to". "Are you ready?" / "Is she old?": after an inverted be the pronoun
  // is followed by an adjective. A capital is a name ("to Batumi").
  for (const raw of texts) {
    raw.forEach((tok, i) => {
      if (i === 0) return;
      const cue = at(raw, i - 1);
      const word = at(raw, i);
      if (!word || /^[A-Z]/u.test(tok) || NOT_VERBS.has(word) || word.endsWith('ly')) return;
      const inverted = PRONOUN_CUES.has(cue) && BE.has(at(raw, i - 2));
      const toVerb = cue === 'to' && TO_AFTER.has(at(raw, i - 2));
      if ((VERB_CUES.has(cue) && !inverted) || toVerb) verbs.add(word);
    });
  }

  // Pass 2 — nouns: the last word of "determiner (word)* word" where the
  // phrase ends (punctuation, a verb, "is", "and"…): "a teacher.", "my knee
  // hurts", "the room and". "a nice day" gives "day", never "nice". A word
  // that ends such phrases less often than it follows be / very / so ("is
  // old", "very nice") is an adjective: "a year old." does not make "old" a
  // noun, while "This is coffee." does not unmake "coffee".
  const nounVotes = new Map<string, number>();
  const vote = (word: string, n: number) => nounVotes.set(word, (nounVotes.get(word) ?? 0) + n);
  for (const raw of texts) {
    raw.forEach((_, i) => {
      const cue = at(raw, i);
      const next = at(raw, i + 1);
      if (next && ADJECTIVE_CUES.has(cue) && !DETERMINERS.has(next)) {
        vote(next, STRONG_ADJECTIVE_CUES.has(cue) ? -2 : -1);
      }
      if (!DETERMINERS.has(cue)) return;
      for (let k = i + 1; k < raw.length && k <= i + 3; k++) {
        const word = at(raw, k);
        if (!word || /^[A-Z]/u.test(raw[k] ?? '')) break;
        const after = at(raw, k + 1);
        const ends =
          /[.,!?;:]$/u.test(raw[k] ?? '') ||
          k === raw.length - 1 ||
          PHRASE_END.has(after) ||
          (isVerbForm(verbs, after) && !verbs.has(word));
        if (ends) {
          if (!NOT_NOUNS.has(word) && !isSuperlative(word)) vote(word, 1);
          break;
        }
      }
    });
  }
  const nouns = new Set<string>(IRREGULAR_NOUNS);
  for (const [word, n] of nounVotes) if (n > 0) nouns.add(word);
  verbCache = verbs;
  nounCache = nouns;
}

export function getVerbWords(): ReadonlySet<string> {
  if (!verbCache) buildClasses();
  return verbCache ?? new Set();
}

/** The words the course uses as NOUNS. With the verbs, the matcher calls an
 *  added -s grammar ("a teachers", "my mothers") only on these two classes —
 *  never on "olds" or "nices" (typos). */
export function getNounWords(): ReadonlySet<string> {
  if (!nounCache) buildClasses();
  return nounCache ?? new Set();
}

function addWords(into: Set<string>, text: string): void {
  for (const raw of normalizeAnswer(text).split(' ')) {
    const w = bareWord(raw);
    if (w) into.add(w);
  }
}

/**
 * Only CORRECT English. Wrong options and tile distractors are left out on
 * purpose — they include made-up traps ("goed", "carefuly", "hotest"), and a
 * learner's typo that hits one is still a typo. Alt answers are left out too:
 * they hold spelling variants ("favorite", "tshirt") of words the main
 * answers already teach.
 */
function englishTexts(ex: Exercise): string[] {
  switch (ex.type) {
    case 'phrase_intro':
      return [ex.en];
    case 'tap_pairs':
      return ex.pairs.map((p) => p.en);
    case 'dialogue':
      return ex.lines.map((l) => l.en);
    case 'translate_tap':
      return ex.answer_tokens;
    case 'translate_typed':
    case 'listen_type':
    case 'choose_correct':
    case 'listen_choose':
      return [ex.answer];
    case 'cloze':
      return [ex.prompt_en.replace('___', ex.answer)];
    case 'phrase_choice':
      // English only: the prompt with the blank filled, plus the answer and
      // options when they are English (a `meaning` item's are Georgian).
      return phraseChoiceEnglishTexts(ex);
    case 'grammar_tip':
      return [];
  }
}
